import { For, Show, createMemo } from 'solid-js';
import { useVim } from './VimProvider';
import { VIM_HINTS } from './types';

/**
 * Vim Status Bar Component
 * Displays current mode, command buffer, and key hints
 */
export function VimStatusBar() {
  const vim = useVim();

  // Get state reactively
  const enabled = createMemo(() => vim.state().enabled);
  const mode = createMemo(() => vim.state().mode);
  const commandBuffer = createMemo(() => vim.state().commandBuffer);
  const currentElement = createMemo(() => vim.state().currentElement);
  const showHelp = createMemo(() => vim.state().showHelp);

  // Mode display names
  const modeNames: Record<string, string> = {
    normal: 'NORMAL',
    insert: 'INSERT',
    command: 'COMMAND',
  };

  // Mode colors
  const modeColors: Record<string, string> = {
    normal: '#4a9eff',
    insert: '#50c878',
    command: '#c084fc',
  };

  return (
    <Show when={enabled()}>
      {/* Main status bar */}
      <div
        class="vim-status-bar"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2rem',
          background: 'var(--card, #1a1a1a)',
          'border-top': '1px solid var(--border, #333)',
          display: 'flex',
          'align-items': 'center',
          padding: '0 1rem',
          'font-family': '"JetBrains Mono", "Fira Code", monospace',
          'font-size': '0.875rem',
          'z-index': 9998,
          gap: '1rem',
        }}
      >
        {/* Mode indicator */}
        <span
          style={{
            color: modeColors[mode()] || '#fff',
            'font-weight': 'bold',
            'min-width': '5rem',
          }}
        >
          {modeNames[mode()] || 'NORMAL'}
        </span>

        {/* Command buffer */}
        <Show when={commandBuffer()}>
          <span
            style={{
              color: 'var(--fg, #fff)',
              'flex-grow': 1,
            }}
          >
            {commandBuffer()}
          </span>
        </Show>

        {/* Current element info */}
        <Show when={currentElement()}>
          <span
            style={{
              color: 'var(--muted-fg, #888)',
              'font-size': '0.75rem',
              'margin-left': 'auto',
            }}
          >
            {currentElement()?.tagName.toLowerCase()}
          </span>
        </Show>

        {/* Help hint */}
        <button
          onClick={() => vim.toggleHelp()}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--muted-fg, #888)',
            cursor: 'pointer',
            'font-size': '0.75rem',
            padding: '0.25rem 0.5rem',
          }}
          aria-label="Toggle vim help"
          title="Press ? to toggle help"
        >
          ? help
        </button>
      </div>

      {/* Help panel */}
      <Show when={showHelp()}>
        <div
          class="vim-help-panel"
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: 0,
            width: '20rem',
            background: 'var(--card, #1a1a1a)',
            border: '1px solid var(--border, #333)',
            'border-right': 'none',
            'border-bottom': 'none',
            'border-radius': '0.5rem 0 0 0',
            padding: '1rem',
            'z-index': 9997,
            'font-family': '"JetBrains Mono", "Fira Code", monospace',
            'font-size': '0.75rem',
            'box-shadow': '0 -4px 20px rgba(0,0,0,0.3)',
          }}
        >
          <div
            style={{
              display: 'flex',
              'justify-content': 'space-between',
              'align-items': 'center',
              'margin-bottom': '0.75rem',
              'padding-bottom': '0.5rem',
              'border-bottom': '1px solid var(--border, #333)',
            }}
          >
            <strong style={{ color: 'var(--fg, #fff)' }}>Vim Navigation</strong>
            <button
              onClick={() => vim.toggleHelp()}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--muted-fg, #888)',
                cursor: 'pointer',
                'font-size': '1rem',
              }}
              aria-label="Close help"
            >
              ×
            </button>
          </div>

          {/* Navigation section */}
          <div style={{ 'margin-bottom': '0.75rem' }}>
            <div
              style={{
                color: 'var(--muted-fg, #888)',
                'margin-bottom': '0.25rem',
                'font-size': '0.625rem',
                'text-transform': 'uppercase',
              }}
            >
              Navigation
            </div>
            <div
              style={{
                display: 'grid',
                'grid-template-columns': 'repeat(2, 1fr)',
                gap: '0.25rem 1rem',
              }}
            >
              <div>
                <kbd style={keyStyle()}>h</kbd> <span style={descStyle()}>left</span>
              </div>
              <div>
                <kbd style={keyStyle()}>j</kbd> <span style={descStyle()}>down</span>
              </div>
              <div>
                <kbd style={keyStyle()}>k</kbd> <span style={descStyle()}>up</span>
              </div>
              <div>
                <kbd style={keyStyle()}>l</kbd> <span style={descStyle()}>right</span>
              </div>
              <div>
                <kbd style={keyStyle()}>gg</kbd> <span style={descStyle()}>top</span>
              </div>
              <div>
                <kbd style={keyStyle()}>G</kbd> <span style={descStyle()}>bottom</span>
              </div>
              <div>
                <kbd style={keyStyle()}>w</kbd> <span style={descStyle()}>next element</span>
              </div>
              <div>
                <kbd style={keyStyle()}>b</kbd> <span style={descStyle()}>prev element</span>
              </div>
            </div>
          </div>

          {/* Actions section */}
          <div style={{ 'margin-bottom': '0.75rem' }}>
            <div
              style={{
                color: 'var(--muted-fg, #888)',
                'margin-bottom': '0.25rem',
                'font-size': '0.625rem',
                'text-transform': 'uppercase',
              }}
            >
              Actions
            </div>
            <div
              style={{
                display: 'grid',
                'grid-template-columns': 'repeat(2, 1fr)',
                gap: '0.25rem 1rem',
              }}
            >
              <div>
                <kbd style={keyStyle()}>Enter</kbd> <span style={descStyle()}>click</span>
              </div>
              <div>
                <kbd style={keyStyle()}>i</kbd> <span style={descStyle()}>input mode</span>
              </div>
              <div>
                <kbd style={keyStyle()}>Esc</kbd>{' '}
                <span style={descStyle()}>close modal / normal mode</span>
              </div>
            </div>
          </div>

          {/* Scroll section */}
          <div style={{ 'margin-bottom': '0.75rem' }}>
            <div
              style={{
                color: 'var(--muted-fg, #888)',
                'margin-bottom': '0.25rem',
                'font-size': '0.625rem',
                'text-transform': 'uppercase',
              }}
            >
              Scroll
            </div>
            <div
              style={{
                display: 'grid',
                'grid-template-columns': 'repeat(2, 1fr)',
                gap: '0.25rem 1rem',
              }}
            >
              <div>
                <kbd style={keyStyle()}>Ctrl+d</kbd> <span style={descStyle()}>half page down</span>
              </div>
              <div>
                <kbd style={keyStyle()}>Ctrl+u</kbd> <span style={descStyle()}>half page up</span>
              </div>
              <div>
                <kbd style={keyStyle()}>Ctrl+f</kbd> <span style={descStyle()}>page down</span>
              </div>
              <div>
                <kbd style={keyStyle()}>Ctrl+b</kbd> <span style={descStyle()}>page up</span>
              </div>
            </div>
          </div>

          {/* Search section */}
          <div>
            <div
              style={{
                color: 'var(--muted-fg, #888)',
                'margin-bottom': '0.25rem',
                'font-size': '0.625rem',
                'text-transform': 'uppercase',
              }}
            >
              Search
            </div>
            <div
              style={{
                display: 'grid',
                'grid-template-columns': 'repeat(2, 1fr)',
                gap: '0.25rem 1rem',
              }}
            >
              <div>
                <kbd style={keyStyle()}>/</kbd> <span style={descStyle()}>search forward</span>
              </div>
              <div>
                <kbd style={keyStyle()}>?</kbd> <span style={descStyle()}>search backward</span>
              </div>
              <div>
                <kbd style={keyStyle()}>n</kbd> <span style={descStyle()}>next result</span>
              </div>
              <div>
                <kbd style={keyStyle()}>N</kbd> <span style={descStyle()}>prev result</span>
              </div>
            </div>
          </div>
        </div>
      </Show>

      {/* Quick hints (shown in normal mode when help is closed) */}
      <Show when={mode() === 'normal' && !showHelp()}>
        <div
          class="vim-hints"
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '1rem',
            display: 'flex',
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: 'var(--card, #1a1a1a)',
            border: '1px solid var(--border, #333)',
            'border-radius': '0.25rem',
            'font-family': '"JetBrains Mono", "Fira Code", monospace',
            'font-size': '0.625rem',
            'z-index': 9996,
            opacity: 0.7,
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = '0.7';
          }}
        >
          <For each={VIM_HINTS.slice(0, 6)}>
            {hint => (
              <span style={{ color: 'var(--muted-fg, #888)' }}>
                <kbd
                  style={{
                    background: 'var(--muted, #333)',
                    padding: '0.125rem 0.25rem',
                    'border-radius': '0.125rem',
                    'font-size': '0.625rem',
                  }}
                >
                  {hint.key}
                </kbd>{' '}
                {hint.label}
              </span>
            )}
          </For>
        </div>
      </Show>
    </Show>
  );
}

// Styles as functions to create new objects
function keyStyle() {
  return {
    background: 'var(--muted, #333)',
    padding: '0.125rem 0.25rem',
    'border-radius': '0.125rem',
    'font-size': '0.625rem',
    color: 'var(--fg, #fff)',
  };
}

function descStyle() {
  return {
    color: 'var(--muted-fg, #888)',
  };
}
